import { activeStepState } from '@/store/atoms/active-step'
import {
  Armchair,
  DotsThreeCircle,
  Hamburger,
  HandHeart,
  Icon,
} from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'

import { CampaignIncentivesSchema } from '@/schema/campaign-incentives.schema'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { CreateIncentive } from '../../../components/create-incentive'

import { useCampaignClient, useIncentiveClient } from '@/lib/api'
import {
  CACHE_KEYS,
  invalidateCampaignQueries,
  invalidateIncentiveQueries,
} from '@/lib/cache-utils'

import type { IncentiveItemFormType } from '@/schema/incentive.schema'

// Define types locally (previously from GraphQL)
type IncentiveCategory = {
  id: string
  name: string
}

import CampaignButtons from '../../../components/campaign-buttons'
import { GetCampaignFragment } from '@/types/campaign'

import IncentiveModal from '@/components/create-incentive/incentive-modal'
import Loading from '@/components/loading'
import { createPortal } from 'react-dom'
import { Chip } from './chip'
import { Card } from './incentive-card'

export default function Incentives({ referral }: { referral: boolean }) {
  const { id } = useParams()
  const { t } = useTranslation()
  const setActiveStep = useSetRecoilState(activeStepState)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const campaignClient = useCampaignClient()
  const incentiveClient = useIncentiveClient()
  const { campaign } = useOutletContext<{ campaign: GetCampaignFragment }>()
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined,
  )
  const [error, setError] = useState<string>()
  const [reuseSameIncentives, setReuseSameIncentives] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editIncentive, setEditIncentive] = useState<
    IncentiveItemFormType & { id?: string }
  >()

  // Fetch incentive categories
  const { data: categoriesResponse } = useQuery({
    queryKey: [CACHE_KEYS.INCENTIVE_CATEGORIES],
    queryFn: async () => await incentiveClient.getCategories.query(),
  })

  const dataCategory =
    categoriesResponse?.status === 200
      ? { incentiveCategories: categoriesResponse.body }
      : null

  // Fetch incentives by category
  const {
    data: incentivesByCategoryResponse,
    isLoading: loadingIncentivesByCategory,
  } = useQuery({
    queryKey: [
      CACHE_KEYS.INCENTIVES_BY_CATEGORY,
      selectedCategory,
      campaign?.customerId,
      id,
    ],
    queryFn: async () =>
      await incentiveClient.getByCategory.query({
        query: {
          incentiveCategoryId: selectedCategory,
          customerId: campaign?.customerId ?? undefined,
          campaignId: id ?? undefined,
        },
      }),
    enabled: !!campaign,
  })

  const incentivesByCategory =
    incentivesByCategoryResponse?.status === 200
      ? { incentivesByCategory: incentivesByCategoryResponse.body }
      : null

  const attachIncentivesMutation = useMutation({
    // ts-rest's `.mutate()` resolves with the parsed response for ANY status
    // code, so a 400 (e.g. gift_group_theme_missing,
    // incentive_customer_mismatch) used to fall through to onSuccess and the
    // user would advance with no error shown. Throw on non-200 so onError
    // sees the structured BadRequestException body.
    mutationFn: async (data: {
      campaignId: string
      incentiveItems: { id: string; skuCode?: string }[]
      referral: boolean
      reuseSameIncentives: boolean
    }) => {
      const res = await campaignClient.attachCampaignIncentives.mutate({
        params: { id: data.campaignId },
        body: data,
      })
      if (res.status !== 200) {
        throw res
      }
      return res
    },
    onSuccess: (_, variables) => {
      // Invalidate campaign queries to reflect the attached incentives
      invalidateCampaignQueries(queryClient, variables.campaignId)
      // Invalidate incentive queries for this campaign
      invalidateIncentiveQueries(queryClient, {
        campaignId: variables.campaignId,
        customerId: campaign?.customerId ?? undefined,
      })
    },
  })

  const [selectedIncentiveItems, setSelectedIncentiveItems] = useState<
    { id: string; skuCode?: string }[]
  >([])

  useEffect(() => {
    const hasReferralIncentives =
      campaign?.referralCampaign && !campaign?.reuseSameIncentives
    const stepNumber = campaign?.referralCampaign
      ? hasReferralIncentives
        ? 5
        : 4
      : 3
    setActiveStep(stepNumber)
  }, [setActiveStep, campaign?.referralCampaign, campaign?.reuseSameIncentives])

  useEffect(() => {
    if (campaign?.campaignIncentives) {
      const filteredIncentives = campaign?.campaignIncentives
        ?.filter(
          (campaignIncentive) =>
            campaignIncentive?.isVisible &&
            (campaign?.reuseSameIncentives ||
              campaignIncentive?.referral === referral),
        )
        .map((campaignIncentive) => ({
          id: campaignIncentive?.incentiveItem.id || '',
          skuCode: campaignIncentive?.incentiveItem?.skuCode || '',
        }))

      setSelectedIncentiveItems(filteredIncentives)
      setReuseSameIncentives(campaign.reuseSameIncentives ?? false)
    }
  }, [campaign?.campaignIncentives, referral, campaign.reuseSameIncentives])

  if (!campaign || loadingIncentivesByCategory) {
    return <Loading />
  }

  const categoryIcons: Record<string, Icon> = {
    personal_care: HandHeart,
    home: Armchair,
    foods_and_drinks: Hamburger,
    other: DotsThreeCircle,
  }

  const onSubmit = (isSaveDraft: boolean) => {
    if (!id || attachIncentivesMutation.isPending) return

    const incentives = {
      campaignId: id,
      incentiveItems: selectedIncentiveItems,
      referral,
      reuseSameIncentives,
    }
    const validatedCampaign = CampaignIncentivesSchema.safeParse(incentives)

    if (!validatedCampaign.success) {
      setError(t('validation_messages.empty_incentive'))
    } else {
      setError('')
      attachIncentivesMutation.mutate(incentives, {
        onSuccess: () => {
          // Only navigate if not saving as draft
          if (!isSaveDraft) {
            if (
              !referral &&
              campaign.referralCampaign &&
              !reuseSameIncentives
            ) {
              navigate(`/campaigns/referral-incentives/${id}`, {
                replace: true,
              })
            } else {
              navigate(`/campaigns/compose-creative/${id}`, {
                replace: false,
              })
            }
          }
        },
        onError: (error: unknown) => {
          console.error('Failed to attach incentives:', error)
          // After the Phase 3 fix, server-side guards throw NestJS
          // exceptions and ts-rest delivers them as
          // { status, body: { error, message, ...extras } }.
          // We try the new structured shape first (treating `error` as an
          // i18n key), then fall back to the legacy JSON-string-in-message
          // shape used by older endpoints, then to a generic fallback.
          const errObj = (error ?? {}) as {
            body?: { error?: string; message?: string; skuCodes?: string }
            message?: string
          }
          const body = errObj.body
          if (body?.error) {
            setError(t(body.error, { sku: body.skuCodes ?? '' }))
            return
          }
          try {
            const parsedError = JSON.parse(errObj.message ?? '') as {
              message: string
              skuCodes: string
            }
            setError(
              t(`${parsedError.message}`, { sku: `${parsedError.skuCodes}` }),
            )
          } catch {
            setError(body?.message ?? t('failed_to_attach_incentives'))
          }
        },
      })
    }
  }

  //TODO find better solution
  const reorderedIncentiveCategories =
    (dataCategory && [
      ...dataCategory.incentiveCategories,
      dataCategory.incentiveCategories[2],
    ]) ||
    []
  if (loadingIncentivesByCategory) return <div>{t('loading')}</div>

  return (
    <div className="w-full">
      <div className="flex flex-row items-center pb-4 gap-2">
        <p className="text-2xl font-bold">{t('incentives.title')}</p>
        {referral && (
          <p className="text-black-400 bg-violet-800 rounded px-1">
            {t('dashboard_labels.referral_campaign')}
          </p>
        )}
      </div>
      <div className="flex flex-row gap-4">
        {reorderedIncentiveCategories.map(
          (cat: IncentiveCategory, index: number) => {
            if (index === 2) return
            return (
              <Chip
                title={t(`incentive_categories.${cat.name}`)}
                IconLeft={categoryIcons[cat.name]}
                id={cat.id}
                key={cat.id}
                onClick={() => {
                  if (selectedCategory === cat.id) {
                    setSelectedCategory(undefined)
                  } else {
                    setSelectedCategory(cat.id)
                  }
                }}
                selectedCategory={selectedCategory}
              />
            )
          },
        )}
      </div>
      <div className="flex flex-wrap gap-6 py-8 w-full ">
        {campaign.campaignCategory === 'lottery' && !referral && (
          <div className="w-full text-sm text-blue-600 bg-blue-50 p-3 rounded mb-3">
            <p>
              {t(
                'For lottery campaigns, incentives must have a SKU code to be selected.',
              )}
            </p>
          </div>
        )}
        <CreateIncentive className="min-h-96 w-64 text-gray-100 border-dashed border-gray-600" />

        {incentivesByCategory?.incentivesByCategory?.length === 0 ? (
          <div>No items available</div>
        ) : (
          <>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped API response */}
            {incentivesByCategory?.incentivesByCategory?.map((inc: any) => (
              <Card
                incentive={{ ...inc }}
                key={inc.id}
                onClick={() => {
                  setEditIncentive({ ...inc })
                  setShowEditModal(true)
                }}
                selectedIncentiveItems={selectedIncentiveItems}
                onClickSelected={() => {
                  // For lottery campaigns, prevent selecting incentives without SKU codes when it is not referral incentive step
                  if (
                    campaign.campaignCategory === 'lottery' &&
                    !referral &&
                    !inc.skuCode
                  ) {
                    return
                  }

                  if (selectedIncentiveItems.some((s) => s.id === inc.id)) {
                    setSelectedIncentiveItems(
                      selectedIncentiveItems.filter(
                        (item) => item.id !== inc.id,
                      ),
                    )
                  } else {
                    setSelectedIncentiveItems([
                      ...selectedIncentiveItems,
                      { id: inc.id, skuCode: inc.skuCode ?? undefined },
                    ])
                  }
                }}
                isLotteryCampaign={
                  campaign.campaignCategory === 'lottery' && !referral
                }
              />
            ))}
          </>
        )}
      </div>
      <div className="flex justify-end text-primary">
        {error && <div>{error}</div>}
      </div>
      {campaign.referralCampaign && !referral && (
        <div className="w-full flex justify-end">
          <label className="">
            <input
              type="checkbox"
              className="mx-2"
              checked={reuseSameIncentives}
              onChange={() => setReuseSameIncentives(!reuseSameIncentives)}
            />
            {t('repeat_incentive')}
          </label>
        </div>
      )}
      <CampaignButtons
        onSaveDraft={() => onSubmit(true)}
        onSubmit={() => onSubmit(false)}
        disableSaveDraft={
          attachIncentivesMutation.isPending ||
          (referral ? true : !!campaign.referralCampaign && reuseSameIncentives)
        }
        disableSubmit={attachIncentivesMutation.isPending}
      />
      {showEditModal &&
        createPortal(
          <IncentiveModal
            onCloseModal={() => setShowEditModal(false)}
            incentiveByModal={editIncentive}
          />,
          document.body,
        )}
    </div>
  )
}
